import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HeaderBar from "../components/layout/Headerbar";
import NavBar from "../components/layout/NavBar";
import LargeButton from "../components/input/LargeButton";
import SmallButton from "../components/input/SmallButton";
import {
  useApiCustomPoiCollection,
  apiCreateCustomPoiCollection,
  apiUpdateCustomPoiCollection,
  apiGetCustomPoiCollectionPois,
  apiUpdateCustomPoiCollectionOrder,
  useApiCustomPois
} from "../api/customPoi";
import { CustomPoiModel } from "../model/customPoi";
import saveIcon from "../assets/add.png";
import backIcon from "../assets/add.png";
import "./CustomPoiCollectionEdit.css";

function CustomPoiCollectionEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    personaId: "",
  });

  const [collectionPois, setCollectionPois] = useState<CustomPoiModel[]>([]);
  const [availablePois, setAvailablePois] = useState<CustomPoiModel[]>([]);
  const [draggedPoi, setDraggedPoi] = useState<CustomPoiModel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const collection = useApiCustomPoiCollection(id || "");
  const allPois = useApiCustomPois();

  const loadCollectionPois = useCallback(async () => {
    if (!isNew && id) {
      try {
        const response = await apiGetCustomPoiCollectionPois(parseInt(id));
        if (response.data) {
          setCollectionPois(response.data);
        }
      } catch (error) {
        console.error("Failed to load collection POIs:", error);
      }
    }
  }, [isNew, id]);

  useEffect(() => {
    if (!isNew && collection.data) {
      setFormData({
        title: collection.data.title || "",
        description: collection.data.description || "",
        personaId: collection.data.personaId?.toString() || "",
      });

      // Load POIs in this collection
      loadCollectionPois();
    }
  }, [collection.data, isNew, loadCollectionPois]);

  useEffect(() => {
    if (allPois.data) {
      // Filter available POIs (not already in collection)
      const collectionPoiIds = new Set(collectionPois.map(poi => poi.id));
      setAvailablePois(allPois.data.filter(poi => !collectionPoiIds.has(poi.id)));
    }
  }, [allPois.data, collectionPois]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (isNew) {
        await apiCreateCustomPoiCollection({
          title: formData.title,
          description: formData.description || undefined,
          personaId: formData.personaId ? parseInt(formData.personaId) : undefined,
        });
      } else if (id) {
        await apiUpdateCustomPoiCollection(parseInt(id), {
          title: formData.title || undefined,
          description: formData.description || undefined,
          personaId: formData.personaId ? parseInt(formData.personaId) : undefined,
        });
      }

      // Update POI order if collection exists
      if (!isNew && id && collectionPois.length > 0) {
        await apiUpdateCustomPoiCollectionOrder(parseInt(id), collectionPois.map(poi => poi.id));
      }

      navigate("/custom-poi-collections");
    } catch (error) {
      console.error("Failed to save collection:", error);
      alert("Failed to save collection. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, poi: CustomPoiModel) => {
    setDraggedPoi(poi);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number, isFromAvailable: boolean) => {
    e.preventDefault();
    if (!draggedPoi) return;

    if (isFromAvailable) {
      // Add POI to collection
      const newCollectionPois = [...collectionPois];
      newCollectionPois.splice(targetIndex, 0, draggedPoi);
      setCollectionPois(newCollectionPois);
    } else {
      // Reorder within collection
      const draggedIndex = collectionPois.findIndex(poi => poi.id === draggedPoi.id);
      if (draggedIndex === -1) return;

      const newCollectionPois = [...collectionPois];
      newCollectionPois.splice(draggedIndex, 1);
      newCollectionPois.splice(targetIndex, 0, draggedPoi);
      setCollectionPois(newCollectionPois);
    }

    setDraggedPoi(null);
  };

  const handleRemovePoi = (poiId: number) => {
    setCollectionPois(prev => prev.filter(poi => poi.id !== poiId));
  };

  const isUserForbidden =
    collection.error?.response?.status === 403 ? true : false;

  if (isUserForbidden) {
    return (
      <div className="app">
        <HeaderBar />
        <NavBar selectedIndex={7} isUserForbidden={true} />
        <div className="content" style={{ padding: 20 }}>
          <div className="grid-item">
            <h1>Insufficient Permissions</h1>
            <p>Please contact your administrator for access.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <HeaderBar>
        <LargeButton
          text="Back to Collections"
          onClick={() => navigate("/custom-poi-collections")}
          icon={backIcon}
          color="#F0EDE0"
        />
        <h1 style={{ marginLeft: 20, display: "inline-block" }}>
          {isNew ? "Create Collection" : "Edit Collection"}
        </h1>
      </HeaderBar>

      <NavBar selectedIndex={7} isUserForbidden={false} />

      <div className="content" style={{ paddingLeft: 16, paddingRight: 16 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Collection Details</h2>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter collection title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter collection description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="personaId">Persona (Optional)</label>
              <select
                id="personaId"
                name="personaId"
                value={formData.personaId}
                onChange={handleInputChange}
              >
                <option value="">General Collection</option>
                {/* TODO: Add persona options from API */}
              </select>
            </div>
          </div>

          {!isNew && (
            <div className="form-section">
              <h2>Manage POIs in Collection</h2>
              <p>Drag POIs from the available list to add them to this collection, or reorder POIs within the collection.</p>

              <div className="poi-management">
                <div className="available-pois">
                  <h3>Available POIs</h3>
                  <div className="poi-list">
                    {availablePois.map((poi, index) => (
                      <div
                        key={poi.id}
                        className="poi-item available-poi"
                        draggable
                        onDragStart={(e) => handleDragStart(e, poi)}
                      >
                        <div className="poi-info">
                          <strong>{poi.title}</strong>
                          <div className="poi-meta">
                            {poi.persona && (
                              <span className="persona-badge">{poi.persona.name}</span>
                            )}
                            <span className={`status-badge status-${poi.status}`}>
                              {poi.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {availablePois.length === 0 && (
                      <div className="empty-state">No available POIs</div>
                    )}
                  </div>
                </div>

                <div className="collection-pois">
                  <h3>POIs in Collection ({collectionPois.length})</h3>
                  <div
                    className="poi-list drop-zone"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 0, true)}
                  >
                    {collectionPois.length === 0 ? (
                      <div className="empty-collection">
                        Drop POIs here to add them to the collection
                      </div>
                    ) : (
                      collectionPois.map((poi, index) => (
                        <div
                          key={poi.id}
                          className="poi-item collection-poi"
                          draggable
                          onDragStart={(e) => handleDragStart(e, poi)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, index, false)}
                        >
                          <div className="poi-info">
                            <strong>{poi.title}</strong>
                            <div className="poi-meta">
                              {poi.persona && (
                                <span className="persona-badge">{poi.persona.name}</span>
                              )}
                              <span className={`status-badge status-${poi.status}`}>
                                {poi.status}
                              </span>
                            </div>
                          </div>
                          <SmallButton
                            text="Remove"
                            onClick={() => handleRemovePoi(poi.id)}
                            className="remove-btn"
                            isOutlineOnly={true}
                            icon={null}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <LargeButton
              text={isSubmitting ? "Saving..." : "Save Collection"}
              onClick={() => handleSubmit()}
              icon={saveIcon}
              color="#F0EDE0"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomPoiCollectionEdit;
